import { Controller } from "@hotwired/stimulus"
import { convertAmount } from "../helpers/unit_conversion"

const SYSTEM_KEY = "recidex:unit-system"

const UNICODE_FRACTIONS = {
  "¼": 0.25, "½": 0.5, "¾": 0.75,
  "⅓": 1 / 3, "⅔": 2 / 3,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875
}

// Meal-size scale steps: quarter, half, then whole multiples going up by one.
// The − / + stepper walks this sequence (¼× ½× 1× 2× 3× …), bottoming out at ¼.
const SCALE_STEPS = [0.25, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 10, 12]

// Scales ingredient quantities by a meal-size multiplier chosen with a − / +
// stepper. 1× is the default and matches what the server rendered. The display
// shows the size (¼× / ½× / 1× / 2× …) plus the resulting servings when the
// recipe has a serving count.
//
// `parseQuantity` is the load-bearing piece — `parseFloat` returns 1 for "1/2"
// and "1 1/2" (it stops at the first non-numeric char), so fraction-string
// quantities silently scaled wrong. We handle Unicode fraction glyphs, plain
// "n/m", mixed "n n/m", and decimals together.
export default class extends Controller {
  static targets = ["scale", "servings", "quantity", "unit", "stepBtn", "field", "systemBtn"]
  static values = { original: Number }

  connect() {
    this.index = SCALE_STEPS.indexOf(1)
    this.system = this.storedSystem()
    this.render()
  }

  // US / metric / as written. Remembered per browser, so a cook who thinks in
  // grams sees every recipe that way.
  setSystem(event) {
    this.system = event.currentTarget.dataset.system || "original"
    try { localStorage.setItem(SYSTEM_KEY, this.system) } catch { /* private mode: fine, just not remembered */ }
    this.render()
  }

  storedSystem() {
    try { return localStorage.getItem(SYSTEM_KEY) || "original" } catch { return "original" }
  }

  step(event) {
    const delta = parseInt(event.currentTarget.dataset.step, 10)
    if (isNaN(delta)) return
    const next = Math.min(SCALE_STEPS.length - 1, Math.max(0, this.index + delta))
    if (next === this.index) return
    this.index = next
    this.render()
  }

  // Current meal-size multiplier — read by other controllers (e.g. the Instacart
  // CTA) via an outlet so they can scale what they send to the server.
  get multiplier() {
    return SCALE_STEPS[this.index]
  }

  render() {
    const m = SCALE_STEPS[this.index]

    if (this.hasScaleTarget) this.scaleTarget.textContent = `${this.formatQuantity(m)}×`

    // Hidden form field (grocery preview) — the raw multiplier the server
    // multiplies ingredient quantities by.
    if (this.hasFieldTarget) this.fieldTarget.value = m

    if (this.originalValue > 0) {
      const n = Math.max(1, Math.round(this.originalValue * m))
      const label = `${n} ${n === 1 ? "serving" : "servings"}`
      this.servingsTargets.forEach((el) => { el.textContent = label })
    }

    this.quantityTargets.forEach((el) => {
      const original = this.parseQuantity(el.dataset.original)
      if (isNaN(original)) return
      const unitEl = el.parentElement?.querySelector("[data-servings-scaler-target~='unit']")
      const converted = unitEl ? convertAmount(original * m, unitEl.dataset.original, this.system) : null
      el.textContent = this.formatQuantity(converted ? converted.amount : original * m)
      if (unitEl) unitEl.textContent = converted ? converted.unit : unitEl.dataset.original
    })

    this.systemBtnTargets.forEach((b) => b.setAttribute("aria-pressed", b.dataset.system === this.system))

    this.stepBtnTargets.forEach((b) => {
      const d = parseInt(b.dataset.step, 10)
      const disabled = (d < 0 && this.index <= 0) || (d > 0 && this.index >= SCALE_STEPS.length - 1)
      b.disabled = disabled
      b.classList.toggle("opacity-50", disabled)
      b.classList.toggle("cursor-not-allowed", disabled)
    })
  }

  parseQuantity(str) {
    if (!str) return NaN
    let s = str.trim()
    for (const [glyph, decimal] of Object.entries(UNICODE_FRACTIONS)) {
      // Insert a separator so "1½" becomes "1 0.5" and parses as 1.5.
      if (s.includes(glyph)) s = s.replaceAll(glyph, ` ${decimal}`)
    }
    s = s.trim().replace(/\s+/g, " ")
    if (!s) return NaN

    let total = 0
    for (const part of s.split(" ")) {
      if (part.includes("/")) {
        const [num, denom] = part.split("/").map(parseFloat)
        if (isNaN(num) || isNaN(denom) || denom === 0) return NaN
        total += num / denom
      } else {
        const n = parseFloat(part)
        if (isNaN(n)) return NaN
        total += n
      }
    }
    return total
  }

  formatQuantity(n) {
    if (n === 0) return "0"
    const fractions = [
      [0.125, "⅛"], [0.25, "¼"], [1 / 3, "⅓"], [0.375, "⅜"],
      [0.5, "½"], [0.625, "⅝"], [2 / 3, "⅔"], [0.75, "¾"], [0.875, "⅞"]
    ]
    const whole = Math.floor(n)
    const remainder = n - whole

    for (const [decimal, symbol] of fractions) {
      if (Math.abs(remainder - decimal) < 0.02) {
        return whole > 0 ? `${whole} ${symbol}` : symbol
      }
    }

    if (remainder < 0.02) return `${whole}`
    return n.toFixed(2).replace(/\.?0+$/, "")
  }
}
